// Screen 9 (spec 13 §Screens, §Friday report; Reports / Ops UX pass 2026-10-21): one week's
// Friday report, read like a document. Two calls on load: the week's versions
// (`GET /reports/friday?week=`) and the stored HTML of the shown version
// (`GET /reports/friday/{id}/html`). The preview is that HTML byte for byte, in an iframe with
// an empty `sandbox` (no scripts, no same-origin, no forms, no popups) through `srcdoc`, so the
// report can neither run code nor reach the app; its SHA-256 is on the frame (`data-sha256`).
//
// The 56 px location header carries the state, the version picker, PDF, Generate / Regenerate
// and Send (managers); Send opens the right drawer (`report-send-drawer.tsx`: recipients, send,
// delivery, schedule), also reached from the list's hover actions (`#send`, `#schedule`).
// Send is refused while the version is not generated, already sent, or `report_stale` (the list
// row's flag on load, review #3, or the API's 422 at send). The supervisor note is a compact
// editor under the document. Client viewers see sent versions only (the API's rule), no actions.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { FileDownIcon, FileTextIcon, SendIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { FridayReportRecord, ReportListItem } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime, REPORT_STATE_LABELS } from "../format";
import { RowAction } from "../ops-ux";
import { keys, reportHtmlQuery, weekReportsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { ReportSendDrawer } from "./report-send-drawer";
import { REPORT_STATE_VARIANT } from "./report-state";
import { CopyLinkAction, usePdfDownload } from "./report-weeks";

/** Hex SHA-256 of a string's UTF-8 bytes (Web Crypto). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

export function ReportPreview({
  api,
  company,
  week,
  pinned = null,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  /**
   * REPORTS-UI: the version shown first (the report email links one version by id), fetched
   * on its own: in the picker even when it is not on the week's first page of versions.
   */
  pinned?: ReportListItem | null;
}) {
  const manager = isManager(company);
  const operator = company.roles.includes("agency_operator");
  const kpi = useFeature("kpi");
  const versions = useQuery(weekReportsQuery(api, company.id, week));
  const [chosen, setChosen] = useState<string | null>(pinned?.id ?? null);
  const [stale, setStale] = useState<ReadonlySet<string>>(new Set());
  // The list's Send / Schedule hover actions land here with `#send` / `#schedule`: the drawer
  // opens with the page.
  const hash = useRouterState({ select: (s) => s.location.hash });
  const [drawer, setDrawer] = useState(() => hash === "send" || hash === "schedule");
  const page = versions.data?.data ?? [];
  const list = [...page, ...(pinned && !page.some((r) => r.id === pinned.id) ? [pinned] : [])].sort(
    (a, b) => b.version - a.version,
  );
  const record = list.find((r) => r.id === chosen) ?? list[0] ?? null;
  const canAct = manager && !kpi.readOnly;
  const onVersion = (r: FridayReportRecord) => {
    setChosen(r.id);
    setStale((s) => {
      const n = new Set(s);
      n.delete(r.id);
      return n;
    });
  };
  const isStale = record ? record.report_stale || stale.has(record.id) : false;

  return (
    <div className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        crumbs={[
          {
            label: "Reports",
            render: (
              <Link to="/c/$slug/kpis/reports" params={{ slug: company.slug }} search={keepFocus} />
            ),
          },
          { label: `W${week.slice(-2)} · ${weekRangeLabel(week)}` },
        ]}
        icon={FileTextIcon}
        title={`Friday report · ${week}`}
        badge={
          record ? (
            <span className="flex items-center gap-1">
              <Badge variant={REPORT_STATE_VARIANT[record.state]} data-slot="report-state">
                {REPORT_STATE_LABELS[record.state]}
              </Badge>
              {canAct && isStale && record.state === "generated" ? (
                <Badge variant="warning">Stale</Badge>
              ) : null}
            </span>
          ) : null
        }
        actions={
          <>
            {record && list.length > 1 ? (
              <NativeSelect
                size="sm"
                aria-label="Version"
                value={record.id}
                onChange={(e) => setChosen(e.target.value)}
                className="w-auto"
              >
                {list.map((r) => (
                  <NativeSelectOption key={r.id} value={r.id}>
                    Version {r.version} · {REPORT_STATE_LABELS[r.state]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            ) : null}
            {record?.pdf_object_key ? (
              <PdfAction api={api} co={company.id} record={record} />
            ) : null}
            <CopyLinkAction slug={company.slug} week={week} />
            {canAct ? (
              <GenerateButton
                api={api}
                co={company.id}
                week={week}
                label={!record || record.state === "draft" ? "Generate" : "Regenerate"}
                onDone={onVersion}
              />
            ) : null}
            {manager ? (
              <Button size="sm" onClick={() => setDrawer(true)} data-slot="open-send">
                <SendIcon aria-hidden="true" />
                Send
              </Button>
            ) : null}
          </>
        }
      />
      {versions.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading the report" rows={4} />
        </div>
      ) : versions.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="The report" error={versions.error} />
        </div>
      ) : !record ? (
        <div className="px-6 py-4 max-md:px-4">
          <EmptyState
            variant="card"
            icon={FileTextIcon}
            title={`No report for ${week}`}
            description={
              manager
                ? "Generate it to preview and send. The scheduled job generates it on Friday."
                : "The report appears here once it has been sent."
            }
            actions={
              canAct ? (
                <GenerateButton
                  api={api}
                  co={company.id}
                  week={week}
                  label="Generate report"
                  onDone={(r) => setChosen(r.id)}
                />
              ) : null
            }
          />
        </div>
      ) : (
        <div className="mx-auto flex w-full max-w-[920px] min-w-0 flex-col gap-4 px-6 py-4 max-md:px-4">
          <div
            className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground tabular-nums"
            data-slot="report-meta"
          >
            <span>Version {record.version}</span>
            {record.generated_at ? (
              <span>Generated {formatDateTime(record.generated_at)}</span>
            ) : null}
            {record.sent_at ? (
              <span>
                Sent {formatDateTime(record.sent_at)} to {record.recipients.length}{" "}
                {record.recipients.length === 1 ? "recipient" : "recipients"}
              </span>
            ) : null}
            {record.send_error ? <span className="text-danger-text">Last send failed</span> : null}
          </div>
          {canAct && isStale && record.state === "generated" ? (
            <Notice tone="warning" title="The report is stale" data-slot="report-stale">
              Hours, costs or numbers of this week changed after it was generated. Regenerate to
              send the current figures, or send it anyway from the Send panel.
            </Notice>
          ) : null}
          <Preview api={api} co={company.id} record={record} />
          {canAct ? (
            <NoteSection
              api={api}
              co={company.id}
              week={week}
              record={record}
              onVersion={onVersion}
            />
          ) : null}
        </div>
      )}
      {manager ? (
        <ReportSendDrawer
          api={api}
          company={company}
          week={week}
          record={record}
          stale={isStale}
          open={drawer}
          onOpenChange={setDrawer}
          onStale={() => record && setStale((s) => new Set(s).add(record.id))}
          canAct={canAct}
          operator={operator}
        />
      ) : null}
    </div>
  );
}

function Preview({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const html = useQuery({
    ...reportHtmlQuery(api, co, record),
    enabled: !!record.html_object_key,
  });
  const [hash, setHash] = useState<{ of: string; hex: string } | null>(null);
  const text = html.data;
  useEffect(() => {
    if (text === undefined) return;
    let live = true;
    void sha256Hex(text).then((hex) => {
      if (live) setHash({ of: text, hex });
    });
    return () => {
      live = false;
    };
  }, [text]);
  if (!record.html_object_key)
    return (
      <EmptyState
        variant="card"
        icon={FileTextIcon}
        title="Not generated yet"
        description="This version is a draft; generate it to see the report."
      />
    );
  if (html.isPending) return <Loading label="Loading the report preview" rows={6} />;
  if (html.isError) return <LoadError what="The report preview" error={html.error} />;
  const hex = hash?.of === html.data ? hash.hex : undefined;
  return (
    <section aria-labelledby="report-preview" className="flex flex-col gap-2">
      <h2 id="report-preview" className="sr-only">
        Preview
      </h2>
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <iframe
          title={`Friday report ${record.week_key}, version ${record.version}`}
          // Empty sandbox: no scripts, no same-origin access, no forms, no popups, no top navigation.
          sandbox=""
          srcDoc={html.data}
          data-sha256={hex}
          className="block h-[72vh] w-full bg-background"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Exactly what the email contains. SHA-256 <code className="break-all">{hex ?? "…"}</code>
      </p>
    </section>
  );
}

function GenerateButton({
  api,
  co,
  week,
  label,
  onDone,
}: {
  api: Api;
  co: string;
  week: string;
  label: string;
  onDone: (r: FridayReportRecord) => void;
}) {
  const qc = useQueryClient();
  const generate = useMutation({
    mutationFn: () => api.client.reports.generate({ params: { co }, body: { week_key: week } }),
    onSuccess: (r) => {
      // `reports(co)` is a prefix of the week's key: one invalidation refetches both once (two
      // would cancel and restart the week's refetch).
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
      void qc.invalidateQueries({ queryKey: keys.reportHtml(co, r.id) });
      onDone(r);
    },
  });
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={generate.isPending}
      onClick={() => generate.mutate()}
    >
      {generate.isPending ? "Generating…" : label}
    </Button>
  );
}

function PdfAction({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const download = usePdfDownload(api, co, record);
  return (
    <RowAction
      icon={FileDownIcon}
      label="Download PDF"
      disabled={download.isPending}
      onClick={() => download.mutate()}
    />
  );
}

function NoteSection({
  api,
  co,
  record,
  onVersion,
}: {
  api: Api;
  co: string;
  week: string;
  record: ReportListItem;
  onVersion: (r: FridayReportRecord) => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState(record.supervisor_note ?? "");
  const [noteFor, setNoteFor] = useState(record.id);
  if (noteFor !== record.id) {
    // Another version is shown: its own note (state adjusted during render, not in an effect).
    setNoteFor(record.id);
    setNote(record.supervisor_note ?? "");
  }
  const saveNote = useMutation({
    mutationFn: () =>
      api.client.reports.update({
        params: { co, id: record.id },
        body: { supervisor_note: note.trim() ? note : null },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
      // Review #2: the edited version's HTML was re-rendered; never show the cached one.
      void qc.invalidateQueries({ queryKey: keys.reportHtml(co, record.id) });
      void qc.invalidateQueries({ queryKey: keys.reportHtml(co, r.id) });
      onVersion(r);
    },
  });
  const noteChanged = (record.supervisor_note ?? "") !== note;
  return (
    <Field data-slot="report-note">
      <div className="flex items-baseline justify-between gap-2">
        <FieldLabel htmlFor="report-note">Supervisor note</FieldLabel>
        <span className="text-xs text-muted-foreground">
          {record.frozen_at
            ? "This version is frozen: saving makes the next version."
            : "Shown at the end of the report."}
        </span>
      </div>
      <Textarea
        id="report-note"
        rows={3}
        maxLength={10_000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        disabled={!noteChanged || saveNote.isPending}
        onClick={() => saveNote.mutate()}
      >
        {saveNote.isPending ? "Saving…" : "Save note"}
      </Button>
    </Field>
  );
}
